(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const getFocusable = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true'
    );

  const debounce = (callback, wait = 180) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const initializeAnnouncementBars = (scope = document) => {
    const bars = scope.matches?.('[data-announcement-bar]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-announcement-bar]') || []);

    bars.forEach((bar) => {
      if (bar.dataset.enhanced === 'true') return;

      const items = Array.from(bar.querySelectorAll('.announcement-bar__item'));
      if (items.length < 2) return;

      bar.dataset.enhanced = 'true';

      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobileRotationQuery = window.matchMedia('(max-width: 46.874rem)');
      const listenerController = new AbortController();
      const listenerOptions = { signal: listenerController.signal };
      let activeIndex = 0;
      let timer = null;
      let pointerPaused = false;
      let focusPaused = false;
      let destroyed = false;

      const stop = () => {
        window.clearTimeout(timer);
        timer = null;
      };

      const cleanup = () => {
        if (destroyed) return;
        destroyed = true;
        stop();
        listenerController.abort();
        reducedMotionQuery.removeEventListener?.('change', handleDisplayPreference);
        mobileRotationQuery.removeEventListener?.('change', handleDisplayPreference);
      };

      const resetItemAccessibility = (item) => {
        item.classList.remove('is-active');
        item.removeAttribute('aria-hidden');
        item.removeAttribute('inert');
        item.querySelectorAll('a, button').forEach((control) => control.removeAttribute('tabindex'));
      };

      const setActiveItem = (nextIndex) => {
        activeIndex = (nextIndex + items.length) % items.length;

        items.forEach((item, index) => {
          const isActive = index === activeIndex;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          item.toggleAttribute('inert', !isActive);
          item.querySelectorAll('a, button').forEach((control) => {
            if (isActive) control.removeAttribute('tabindex');
            else control.tabIndex = -1;
          });
        });
      };

      const isPaused = () => pointerPaused || focusPaused || document.hidden;

      const schedule = () => {
        stop();
        if (destroyed || reducedMotionQuery.matches || !mobileRotationQuery.matches || isPaused()) return;

        timer = window.setTimeout(() => {
          if (!bar.isConnected) {
            cleanup();
            return;
          }
          setActiveItem(activeIndex + 1);
          schedule();
        }, 5200);
      };

      const enableRotation = () => {
        bar.classList.add('is-rotating');
        setActiveItem(activeIndex);
        schedule();
      };

      const disableRotation = () => {
        stop();
        bar.classList.remove('is-rotating');
        items.forEach(resetItemAccessibility);
      };

      const handleDisplayPreference = () => {
        if (reducedMotionQuery.matches || !mobileRotationQuery.matches) disableRotation();
        else enableRotation();
      };

      bar.addEventListener('pointerenter', () => {
        pointerPaused = true;
        stop();
      }, listenerOptions);

      bar.addEventListener('pointerleave', () => {
        pointerPaused = false;
        schedule();
      }, listenerOptions);

      bar.addEventListener('focusin', () => {
        focusPaused = true;
        stop();
      }, listenerOptions);

      bar.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          focusPaused = bar.contains(document.activeElement);
          schedule();
        });
      }, listenerOptions);

      document.addEventListener('visibilitychange', schedule, listenerOptions);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === bar || event.target?.contains?.(bar)) cleanup();
      }, listenerOptions);
      reducedMotionQuery.addEventListener?.('change', handleDisplayPreference, listenerOptions);
      mobileRotationQuery.addEventListener?.('change', handleDisplayPreference, listenerOptions);
      handleDisplayPreference();
    });
  };

  const closeDetails = (details, restoreFocus = false) => {
    if (!details || !details.open) return;
    const focusWasInside = details.contains(document.activeElement);
    details.open = false;
    if (restoreFocus && focusWasInside) details.querySelector(':scope > summary')?.focus();
  };

  const updateBodyLock = () => {
    document.body.classList.toggle('drawer-open', Boolean(document.querySelector('[data-drawer][open]')));
  };

  const setDrawerTop = (header) => {
    if (!header || !header.isConnected) return;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const bottom = Math.min(viewportHeight, Math.max(0, header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--drawer-top', `${bottom}px`);
  };

  const initializeHeader = (scope = document) => {
    const header = scope.matches?.('[data-site-header]') ? scope : scope.querySelector('[data-site-header]');
    if (!header || header.dataset.enhanced === 'true') return;
    header.dataset.enhanced = 'true';

    const drawers = Array.from(header.querySelectorAll('[data-drawer]'));
    const menuDisclosures = Array.from(header.querySelectorAll('[data-menu-disclosure]'));
    const menuDrawer = header.querySelector('.header-drawer--menu');
    const searchDrawer = header.querySelector('.header-drawer--search');
    const mobileSearchTriggers = Array.from(header.querySelectorAll('[data-open-header-search]'));

    mobileSearchTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (!searchDrawer) return;
        event.preventDefault();
        closeDetails(menuDrawer);
        searchDrawer.dataset.openedFromMobileMenu = 'true';
        searchDrawer.open = true;
      });
    });

    const updateHeaderState = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
      if (drawers.some((drawer) => drawer.open)) setDrawerTop(header);
    };

    setDrawerTop(header);
    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    window.addEventListener(
      'resize',
      debounce(() => {
        if (window.matchMedia('(min-width: 61.875rem)').matches) {
          closeDetails(header.querySelector('.header-drawer--menu'));
          updateBodyLock();
        }
        setDrawerTop(header);
      }, 100),
      { passive: true }
    );

    drawers.forEach((drawer) => {
      const summary = drawer.querySelector(':scope > summary');
      const panel = drawer.querySelector(':scope > .header-drawer__panel');

      drawer.addEventListener('toggle', () => {
        if (drawer.open) {
          drawers.forEach((otherDrawer) => {
            if (otherDrawer !== drawer) closeDetails(otherDrawer);
          });
          menuDisclosures.forEach((disclosure) => closeDetails(disclosure));
          setDrawerTop(header);
          window.requestAnimationFrame(() => {
            const firstField = panel?.querySelector('input, button, a[href]');
            firstField?.focus({ preventScroll: true });
          });
        }

        summary?.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');
        if (drawer === searchDrawer) {
          mobileSearchTriggers.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');
          });
          if (!drawer.open) delete drawer.dataset.openedFromMobileMenu;
        }
        updateBodyLock();
      });

      panel?.querySelectorAll('[data-close-details]').forEach((button) => {
        button.addEventListener('click', () => {
          const returnToMenuTrigger = drawer === searchDrawer && drawer.dataset.openedFromMobileMenu === 'true';
          closeDetails(drawer, !returnToMenuTrigger);
          if (returnToMenuTrigger) menuDrawer?.querySelector(':scope > summary')?.focus();
        });
      });

      panel?.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = getFocusable(panel);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });

    menuDisclosures.forEach((disclosure) => {
      disclosure.addEventListener('toggle', () => {
        if (!disclosure.open) return;
        menuDisclosures.forEach((otherDisclosure) => {
          if (otherDisclosure !== disclosure) closeDetails(otherDisclosure);
        });
      });
    });

    document.addEventListener('pointerdown', (event) => {
      drawers.forEach((drawer) => {
        if (!drawer.open) return;
        const panel = drawer.querySelector(':scope > .header-drawer__panel');
        const summary = drawer.querySelector(':scope > summary');
        if (!panel?.contains(event.target) && !summary?.contains(event.target)) closeDetails(drawer, true);
      });

      menuDisclosures.forEach((disclosure) => {
        if (disclosure.open && !disclosure.contains(event.target)) closeDetails(disclosure);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const activeDrawer = drawers.find((drawer) => drawer.open);
      if (activeDrawer) {
        const returnToMenuTrigger = activeDrawer === searchDrawer && activeDrawer.dataset.openedFromMobileMenu === 'true';
        closeDetails(activeDrawer, !returnToMenuTrigger);
        if (returnToMenuTrigger) menuDrawer?.querySelector(':scope > summary')?.focus();
        return;
      }
      menuDisclosures.forEach((disclosure) => closeDetails(disclosure, true));
    });
  };

  const initializeFooter = (scope = document) => {
    const footers = scope.matches?.('.site-footer')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('.site-footer') || []);

    footers.forEach((footer) => {
      if (footer.dataset.enhanced === 'true') return;
      footer.dataset.enhanced = 'true';

      const disclosures = Array.from(footer.querySelectorAll('.site-footer__disclosure'));
      const desktopQuery = window.matchMedia('(min-width: 46.875rem)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      const resetAnimation = (disclosure) => {
        const content = disclosure.querySelector(':scope > .site-footer__disclosure-content');
        if (!content) return;
        content.style.removeProperty('height');
        content.style.removeProperty('opacity');
        disclosure.dataset.animating = 'false';
      };

      const setExpandedState = (disclosure, expanded) => {
        const summary = disclosure.querySelector(':scope > .site-footer__disclosure-summary');
        summary?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      const animateDisclosure = (disclosure, shouldOpen) => {
        const content = disclosure.querySelector(':scope > .site-footer__disclosure-content');
        if (!content || disclosure.dataset.animating === 'true') return;

        if (reducedMotionQuery.matches) {
          disclosure.open = shouldOpen;
          setExpandedState(disclosure, shouldOpen);
          resetAnimation(disclosure);
          return;
        }

        disclosure.dataset.animating = 'true';

        if (shouldOpen) {
          disclosure.open = true;
          setExpandedState(disclosure, true);
          content.style.height = '0px';
          content.style.opacity = '0';

          window.requestAnimationFrame(() => {
            content.style.height = `${content.scrollHeight}px`;
            content.style.opacity = '1';
          });
        } else {
          setExpandedState(disclosure, false);
          content.style.height = `${content.getBoundingClientRect().height}px`;
          content.style.opacity = '1';

          window.requestAnimationFrame(() => {
            content.style.height = '0px';
            content.style.opacity = '0';
          });
        }

        const finishAnimation = (event) => {
          if (event.propertyName !== 'height') return;
          content.removeEventListener('transitionend', finishAnimation);
          if (!shouldOpen) disclosure.open = false;
          resetAnimation(disclosure);
        };

        content.addEventListener('transitionend', finishAnimation);
      };

      const synchronizeLayout = () => {
        disclosures.forEach((disclosure) => {
          const summary = disclosure.querySelector(':scope > .site-footer__disclosure-summary');
          resetAnimation(disclosure);

          if (desktopQuery.matches) {
            disclosure.open = true;
            setExpandedState(disclosure, true);
            summary?.setAttribute('aria-disabled', 'true');
            if (summary) summary.tabIndex = -1;
          } else {
            disclosure.open = false;
            setExpandedState(disclosure, false);
            summary?.removeAttribute('aria-disabled');
            if (summary) summary.removeAttribute('tabindex');
          }
        });
      };

      disclosures.forEach((disclosure) => {
        const summary = disclosure.querySelector(':scope > .site-footer__disclosure-summary');
        summary?.addEventListener('click', (event) => {
          event.preventDefault();
          if (desktopQuery.matches) return;
          animateDisclosure(disclosure, !disclosure.open);
        });
      });

      desktopQuery.addEventListener?.('change', synchronizeLayout);
      synchronizeLayout();
    });
  };

  const initializeFaq = (scope = document) => {
    const faqDetails = scope.matches?.('.faq-directory__question, .sr-faq details')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('.faq-directory__question, .sr-faq details') || []);
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    faqDetails.forEach((details, index) => {
      if (details.dataset.faqEnhanced === 'true') return;

      const summary = details.querySelector(':scope > summary');
      const answer = details.querySelector(':scope > .faq-directory__answer, :scope > .sr-faq__answer');
      if (!summary || !answer) return;

      details.dataset.faqEnhanced = 'true';
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      const section = details.closest('[data-section-id], .shopify-section');
      const sectionKey = section?.dataset.sectionId || section?.id || 'faq';
      const idKey = `${sectionKey.replace(/[^a-zA-Z0-9_-]/g, '')}-${index + 1}`;
      const content = document.createElement('div');
      content.className = 'faq-motion__content';
      content.id = `FaqAnswer-${idKey}`;
      if (!summary.id) summary.id = `FaqSummary-${idKey}`;
      answer.before(content);
      content.append(answer);
      summary.setAttribute('aria-controls', content.id);

      let transitionCleanup = null;

      const clearTransition = () => {
        transitionCleanup?.();
        transitionCleanup = null;
      };

      const finishAfterTransition = (callback) => {
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          content.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(timer);
          callback();
        };
        const onTransitionEnd = (event) => {
          if (event.target === content && event.propertyName === 'height') finish();
        };
        const timer = window.setTimeout(finish, 460);
        content.addEventListener('transitionend', onTransitionEnd);
        transitionCleanup = () => {
          content.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(timer);
        };
      };

      const resetAnswerStyles = () => {
        content.style.removeProperty('height');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
        details.removeAttribute('data-faq-animating');
      };

      const setOpenImmediately = (shouldOpen) => {
        clearTransition();
        details.open = shouldOpen;
        summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        resetAnswerStyles();
      };

      const expand = () => {
        const isReversing = details.dataset.faqAnimating === 'closing';
        const startHeight = isReversing ? content.getBoundingClientRect().height : 0;
        clearTransition();
        details.open = true;
        details.dataset.faqAnimating = 'opening';
        summary.setAttribute('aria-expanded', 'true');

        content.style.height = `${startHeight}px`;
        content.style.opacity = startHeight > 0 ? getComputedStyle(content).opacity : '0';
        content.style.transform = startHeight > 0 ? getComputedStyle(content).transform : 'translateY(-0.35rem)';

        window.requestAnimationFrame(() => {
          content.style.height = `${content.scrollHeight}px`;
          content.style.opacity = '1';
          content.style.transform = 'translateY(0)';
        });

        finishAfterTransition(resetAnswerStyles);
      };

      const collapse = () => {
        clearTransition();
        details.dataset.faqAnimating = 'closing';
        summary.setAttribute('aria-expanded', 'false');

        content.style.height = `${content.getBoundingClientRect().height || content.scrollHeight}px`;
        content.style.opacity = getComputedStyle(content).opacity;
        content.style.transform = getComputedStyle(content).transform;

        window.requestAnimationFrame(() => {
          content.style.height = '0px';
          content.style.opacity = '0';
          content.style.transform = 'translateY(-0.35rem)';
        });

        finishAfterTransition(() => {
          details.open = false;
          resetAnswerStyles();
        });
      };

      summary.addEventListener('click', (event) => {
        if (event.button !== 0 && event.detail !== 0) return;
        event.preventDefault();

        if (reducedMotionQuery.matches) {
          setOpenImmediately(!details.open);
          return;
        }

        if (details.dataset.faqAnimating === 'closing' || !details.open) expand();
        else collapse();
      });

      details.addEventListener('toggle', () => {
        if (!details.hasAttribute('data-faq-animating')) {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        }
      });
    });
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.abortController = null;
      this.search = debounce(this.search.bind(this));
    }

    connectedCallback() {
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', this.search);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.clear();
      });
    }

    clear() {
      this.abortController?.abort();
      this.results.replaceChildren();
      this.results.hidden = true;
      this.removeAttribute('aria-busy');
      if (this.status) this.status.textContent = '';
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.clear();
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = 'Zoeken…';

      try {
        const base = this.dataset.url || '/search/suggest';
        const endpoint = base.endsWith('.json') ? base : `${base}.json`;
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const payload = await response.json();
        this.render(payload.resources?.results || {}, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.replaceChildren(this.createMessage('Zoeken lukt op dit moment niet. Gebruik de zoekknop om verder te gaan.', 'predictive-search__error'));
        this.results.hidden = false;
        if (this.status) this.status.textContent = 'Zoeken is niet beschikbaar.';
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    createMessage(text, className) {
      const message = document.createElement('p');
      message.className = className;
      message.textContent = text;
      return message;
    }

    createResult(item, typeLabel) {
      const listItem = document.createElement('li');
      listItem.className = 'predictive-search__item';

      const link = document.createElement('a');
      link.href = item.url;

      const imageUrl = item.featured_image?.url || item.image?.url;
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'predictive-search__image';
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.image?.alt || '';
        image.width = 96;
        image.height = 96;
        image.loading = 'lazy';
        link.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'predictive-search__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        link.append(placeholder);
      }

      const title = document.createElement('span');
      title.className = 'predictive-search__item-title';
      title.textContent = item.title;
      link.append(title);

      const type = document.createElement('span');
      type.className = 'predictive-search__item-type';
      type.textContent = typeLabel;
      link.append(type);

      listItem.append(link);
      return listItem;
    }

    render(groups, query) {
      const labels = {
        products: 'Product',
        collections: 'Collectie',
        pages: 'Pagina',
        articles: 'Advies'
      };
      const fragment = document.createDocumentFragment();
      let resultCount = 0;

      Object.entries(labels).forEach(([key, singularLabel]) => {
        const items = Array.isArray(groups[key]) ? groups[key] : [];
        if (!items.length) return;

        const group = document.createElement('section');
        group.className = 'predictive-search__group';

        const heading = document.createElement('h3');
        heading.className = 'predictive-search__group-title';
        heading.textContent = key === 'products' ? 'Producten' : key === 'collections' ? 'Collecties' : key === 'articles' ? 'Advies' : 'Pagina’s';
        group.append(heading);

        const list = document.createElement('ul');
        list.className = 'predictive-search__list';
        list.setAttribute('role', 'list');
        items.forEach((item) => {
          list.append(this.createResult(item, singularLabel));
          resultCount += 1;
        });
        group.append(list);
        fragment.append(group);
      });

      if (!resultCount) {
        fragment.append(this.createMessage(`Geen directe resultaten voor “${query}”.`, 'predictive-search__empty'));
      }

      const allResults = document.createElement('a');
      allResults.className = 'button button--secondary predictive-search__all';
      const formAction = this.querySelector('form')?.action || '/search';
      const searchUrl = new URL(formAction, window.location.origin);
      searchUrl.searchParams.set('q', query);
      allResults.href = `${searchUrl.pathname}${searchUrl.search}`;
      allResults.textContent = resultCount ? `Bekijk alle resultaten voor “${query}”` : `Zoek toch naar “${query}”`;
      fragment.append(allResults);

      this.results.replaceChildren(fragment);
      this.results.hidden = false;
      if (this.status) this.status.textContent = `${resultCount} suggesties gevonden.`;
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  initializeAnnouncementBars();
  initializeHeader();
  initializeFooter();
  initializeFaq();
  document.addEventListener('shopify:section:load', (event) => {
    initializeAnnouncementBars(event.target);
    initializeHeader(event.target);
    initializeFooter(event.target);
    initializeFaq(event.target);
  });
})();
